import type * as pfWilo from '@amirada/lib-wilo-shared'
import { ExtCmdType } from '@amirada/lib-wilo-shared'
import type { ECarts } from '@base/utils/constants'
import { useCartStore } from '@base/stores/cartStore'
import type { IOpenProject, IStoredProject } from '~/utils/types'
import { type TFetchData, usePfinderAPI } from '~/composables/pfinderAPI'
import { ut_wilo_accountId } from '~/utils/utils'
import { ut_wilo_projectUrlMatches, ut_wilo_setProjectUrl } from '~/utils/priceList'

/*
 * Auto-save for the price-list projects (24.08.2026).
 *
 * The rule the customer asked for: "empty or enable edit leads to saving". Becoming editable IS the
 * moment a project comes into being — a custom price list the second its name is confirmed (it is
 * created before it even opens, see `create`), a template project the second "Enable edit" is
 * pressed. A template that is only looked at stays locked and therefore leaves nothing behind.
 *
 * Everything after that is ordinary: a change to the cart while a project is open and editable.
 * Opening a template fills the cart while it is still locked, so that change passes by unsaved.
 *
 * Three timings, and each covers a different way of losing work:
 *   debounce 2 s   ten hide-clicks in a row are ONE request, not ten
 *   ceiling 20 s   someone who keeps clicking never falls quiet, so a debounce alone would never
 *                  fire — this guarantees a save while work is going on
 *   events         leaving the project, hiding the tab, closing the window: the seconds between the
 *                  last change and the next tick
 *
 * No local buffer on purpose (decision of 24.08.2026): the tool is used by Wilo employees at a desk
 * on a stable network, and a second copy in local storage would be a second truth. The cost is
 * stated plainly — if a save fails, the stretch since the last successful one is gone.
 */

const def_debounceMs: number = 2000
const def_ceilingMs: number = 20000

/*
 * Every request here runs in the background, without the app's loading overlay (14.10.2026,
 * Christoph): a save every few seconds must not grey out the page someone is working on. What saving
 * is doing is shown by `saveState` next to the project name instead.
 *
 * ⚑ `fetchData` never throws — a failure answers `undefined` (and shows its own warning). So every
 * call here checks the answer itself; a `catch` alone would book a failed save as saved.
 */
/*
 * The same goes for a server that accepts the connection and never answers: without a limit the save
 * would wait for ever, "Saving…" would stand for good, and `saving` would hold off every later
 * attempt. 15 s is far above a normal save and short enough that the retry loop takes over.
 */
const def_fetchOpts: TFetchData = { usePost: true, noLoading: true, timeout: 15000 }

export type TSaveState = 'idle' | 'saving' | 'saved' | 'error'

/*
 * Renaming a saved project — the ONE way, for the open project (useProjectAutoSave#rename) and for a
 * tile in the project management (CartManage) alike. `CART_UPDATE` with only a name: the data on the
 * server stays as it is. Answers whether the server took it (fetchData answers `undefined` instead of
 * throwing, and has shown its warning by then).
 */
export function useProjectRename() {
  const pfinderAPI = usePfinderAPI()

  async function renameStored(uuid: string, name: string): Promise<boolean> {
    const args: pfWilo.ExtCmd<ExtCmdType.CART_UPDATE>['arg'] = { uuid, name }
    return !!(await pfinderAPI.fetchData(ExtCmdType.CART_UPDATE, args, def_fetchOpts))
  }

  return { renameStored }
}

export function useProjectAutoSave(cart: ECarts, project: Ref<IOpenProject | undefined>) {
  const cartStore = useCartStore()
  const pfinderAPI = usePfinderAPI()

  const saveState: Ref<TSaveState> = ref('idle')
  const { renameStored } = useProjectRename()

  let debounceTimer: ReturnType<typeof setTimeout> | undefined
  let ceilingTimer: ReturnType<typeof setTimeout> | undefined
  let saving: boolean = false
  let dirty: boolean = false

  const accountId = computed((): string => ut_wilo_accountId())

  /*
   * What actually gets saved — and at the same time the answer to "did anything change?".
   *
   * `stripCart(…, false)` carries the rows with their `userDefined`: the price tables, the hidden
   * flags and the structure markers all live in there. Beside them travel the PDF settings the modal
   * was left with — being part of this string is also what makes a changed setting save itself, with
   * no call needed at the place that changed it.
   *
   * Comparing the serialised form makes "changed" a fact rather than a feeling, so a reactive touch
   * that changes nothing (a re-render, a refresh returning the same data) does not produce a request.
   *
   * Takes the project rather than reading the open one, because `create` writes one that is not open
   * yet.
   */
  function serialiseFor(open: IOpenProject | undefined): string {
    const stored: IStoredProject = {
      items: cartStore.stripCart(cartStore.getCart(cart), false) || [],
      pdf: open?.pdf,
    }
    return JSON.stringify(stored)
  }

  const serialise = (): string => serialiseFor(project.value)

  /*
   * ⚑ A watcher, against the house rule of preferring a direct call — deliberately. The alternative
   * is a `touch()` at every place that changes the cart: every hide, delete, insert, rename, price
   * table. One forgotten call there is a project that silently stops saving, and that is a worse
   * failure than the watcher's cost.
   */
  watch(serialise, (now, before) => {
    if (now === before) { return }
    if (!project.value || !project.value.editable) { return }
    schedule()
  })

  /*
   * "Enable edit" on a template project is itself a save — that is the customer's rule, and it is why
   * a template only appears under "My Projects" once it has been unlocked.
   *
   * Written out immediately rather than debounced: the click is a deliberate act and should show its
   * result. A project that already has an id changed nothing by being unlocked, so it stays quiet —
   * and that is every other project: a saved one opens with its id, and so does a custom price list,
   * which `create` has put on the server before it opened.
   */
  watch(() => project.value?.editable === true, (editable) => {
    if (editable && project.value && !project.value.id) {
      dirty = true
      void flush()
    }
  })

  function schedule(): void {
    dirty = true
    if (debounceTimer) { clearTimeout(debounceTimer) }
    debounceTimer = setTimeout(() => void flush(), def_debounceMs)
    // Started only once per run of changes: it is the guarantee that a continuous worker still
    // gets saved, not a second debounce.
    if (!ceilingTimer) { ceilingTimer = setTimeout(() => void flush(), def_ceilingMs) }
  }

  function clearTimers(): void {
    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = undefined
    }
    if (ceilingTimer) {
      clearTimeout(ceilingTimer)
      ceilingTimer = undefined
    }
  }

  /*
   * Write it out. Never two at once: a second request started while the first is in flight can
   * answer first, and the older state would win. Anything that arrives meanwhile is remembered as
   * dirty and saved right after.
   */
  async function flush(): Promise<void> {
    clearTimers()
    const open: IOpenProject | undefined = project.value
    /*
     * ⚑ `ut_wilo_projectUrlMatches` is the gate that keeps a project from writing while it is not the
     * one on screen. Going back leaves the project in memory — deliberately, so forward shows it
     * again without loading. It is the SECOND line only: the browser's forward button can put the old
     * address back while another project is loading into the cart (measured 15.09.2026, project A got
     * B's rows). The first line is that every opening lets go of the old project before it touches
     * the cart (`usePriceListProject#releaseProject`) — with no project open, nothing is written.
     */
    if (!dirty || saving || !open || !accountId.value || !ut_wilo_projectUrlMatches(open)) { return }

    saving = true
    dirty = false
    saveState.value = 'saving'

    try {
      if (open.id) {
        const args: pfWilo.ExtCmd<ExtCmdType.CART_UPDATE>['arg'] = { uuid: open.id, data: serialise() }
        const res = await pfinderAPI.fetchData(ExtCmdType.CART_UPDATE, args, def_fetchOpts)
        if (!res) { throw new Error('CART_UPDATE answered nothing') }
      }
      else {
        // First save of a template project, on "Enable edit" — it comes into being here, which is why
        // a template that was only opened and left never appears under "My Projects".
        const uuid: string = await create(open)
        if (!uuid) { throw new Error('CART_CREATE answered no uuid') }
        // The uuid turns the project into a saved one; every further save is an update.
        // ⚑ Written back into the object the caller holds — without it the next save would create a
        // second record, and a third, one per change.
        if (project.value) {
          project.value.id = uuid
          /*
           * The address stood on the placeholder until now — the project did not exist yet, so there
           * was nothing to point at. From here it does, and a reload or a bookmark should find it.
           *
           * Replace, not push: this is a correction of the entry the opening already made, not a
           * step of its own. Pushing would put a second entry in front of the back button that
           * leads to the same page.
           */
          ut_wilo_setProjectUrl(uuid, false)
        }
      }
      saveState.value = 'saved'
    }
    catch (err) {
      // Put it back on the pile: the next change, tick or exit tries again.
      dirty = true
      saveState.value = 'error'
      console.error('Auto-save failed:', (err as Error).message)
    }
    finally {
      saving = false
      if (dirty) { schedule() }
    }
  }

  /*
   * Put a project on the server for the first time and answer its uuid — or '' when that did not
   * happen: no account, or no answer (`fetchData` never throws, it answers `undefined`).
   *
   * The one place a record is created, reached two ways: `flush` for a template on "Enable edit", and
   * `usePriceListProject#openProject` for a custom price list, which exists from the moment its name is confirmed
   * and is therefore created before it opens. It touches neither the address nor the open project;
   * that is up to the caller.
   */
  async function create(open: IOpenProject): Promise<string> {
    if (!accountId.value) { return '' }
    const args: pfWilo.ExtCmd<ExtCmdType.CART_CREATE>['arg'] = {
      cart: serialiseFor(open),
      type: cart,
      name: open.name,
      groupId: accountId.value,
    }
    const res = await pfinderAPI.fetchData(ExtCmdType.CART_CREATE, args, def_fetchOpts)
    return (res && res.result) || ''
  }

  /*
   * Renaming. `CART_UPDATE` takes a name on its own, so this touches nothing else — the data stays
   * as it is on the server.
   *
   * A project that has no id yet only gets the name locally: it is not on the server, and the create
   * that follows carries the new name with it.
   *
   * A saved project takes the new name only once the server has it — failed, it keeps the old one,
   * so the name on screen is never one the project management would not show.
   */
  async function rename(name: string): Promise<void> {
    const open: IOpenProject | undefined = project.value
    if (!open || !name || name === open.name) { return }
    if (!open.id) {
      open.name = name
      return
    }
    if (await renameStored(open.id, name)) { open.name = name }
  }

  // The tab going away is the last moment we can still write properly.
  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') { void flush() }
  }

  onMounted(() => {
    if (import.meta.client) {
      document.addEventListener('visibilitychange', onVisibility)
      // Best effort only: the browser will not wait for a request here. It costs nothing and
      // catches the case where the debounce has not fired yet.
      window.addEventListener('beforeunload', onVisibility)
    }
  })

  onBeforeUnmount(() => {
    if (import.meta.client) {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('beforeunload', onVisibility)
    }
    void flush()
    clearTimers()
  })

  return { saveState, flush, create, rename }
}
